import React from 'react';
import { Box, Stack } from '@mui/material';
import { SlideBlockView } from './SlideBlockView';
import type { ReportBlock } from '../api/types';
import type { ReportBranding } from '../utils/branding';

interface ReportPageViewProps {
  blocks: ReportBlock[];
  clientId: string | null;
  branding?: ReportBranding;
  /** print = static layout for PDF/PPTX; screen = presentation slide. */
  variant?: 'screen' | 'print';
}

/**
 * Read-only render of one report page's blocks, via the shared SlideBlockView
 * dispatcher. Used by presentation mode and the print route so the exported
 * pack matches what's presented on screen.
 */
export const ReportPageView: React.FC<ReportPageViewProps> = ({ blocks, clientId, branding, variant = 'screen' }) => {
  // Only pages that contain an image need a full-height flex column (the image
  // flexes into whatever the other blocks don't use). Pages without images keep
  // content-driven height so SlideFrame's vertical centering still applies.
  const hasImage = blocks.some((block) => block.type === 'image');

  return (
    <Stack spacing={3} sx={{ width: '100%', ...(hasImage && { height: '100%' }) }}>
      {blocks.map((block) => {
        const view = (
          <SlideBlockView
            key={block.id}
            block={block}
            clientId={clientId}
            branding={branding}
            fill={variant === 'screen'}
          />
        );
        // Image blocks get the remaining vertical space; every other block
        // keeps its intrinsic height.
        return block.type === 'image' ? (
          <Box key={block.id} sx={{ flex: 1, minHeight: 0 }}>
            {view}
          </Box>
        ) : (
          view
        );
      })}
    </Stack>
  );
};
